
import { createRoot } from 'react-dom/client'
import { StrictMode, lazy, Suspense } from 'react'
import App from './App.tsx'
import './index.css'

// Measure First Input Delay
let firstInputDelay = -1;
const firstInputObserver = () => {
  const onFirstInput = (event: Event) => {
    // Only report if FID wasn't reported before
    if (firstInputDelay < 0) {
      const now = performance.now();
      const delay = now - event.timeStamp;
      
      firstInputDelay = delay;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('First Input Delay:', Math.round(delay), 'ms');
      }
      
      // Remove listeners after we get the first input
      ['mousedown', 'keydown', 'touchstart', 'pointerdown'].forEach(
        type => window.removeEventListener(type, onFirstInput, { capture: true })
      );
    }
  };
  
  // Fix: Use proper TypeScript types for event listeners
  ['mousedown', 'keydown', 'touchstart', 'pointerdown'].forEach(
    type => window.addEventListener(type, onFirstInput, { capture: true })
  );
};

// Initialize metrics collection if in browser environment
if (typeof window !== 'undefined') {
  firstInputObserver();
  
  // Disable browser's automatic scroll restoration to prevent interference
  // with SPA navigation and portaled components (like Radix Select).
  // We handle scroll-to-top explicitly where needed.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
}

// Lazy load the SpeedInsights to improve initial load time
const SpeedInsights = lazy(() => import('@vercel/speed-insights/react').then(module => ({ 
  default: module.SpeedInsights 
})));

// Render the application with a wrapper to avoid layout shifts
const renderApp = () => {
  const rootElement = document.getElementById("root");
  
  if (!rootElement) {
    console.error("Root element not found");
    return;
  }
  
  // Set initial height to prevent layout shift
  rootElement.style.minHeight = '100vh';
  
  const root = createRoot(rootElement);
  
  root.render(
    <StrictMode>
      <App />
      <Suspense fallback={null}>
        <SpeedInsights />
      </Suspense>
    </StrictMode>
  );
};

// Use requestIdleCallback for non-critical init
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    // Monitoring and analytics can be initialized here
    console.log('Initializing non-critical features during idle time');
  });
}

// Start rendering
renderApp();

// Remove console logs in production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
}
